import { Component, ViewChild } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { LinkSubjectToTeamService, Pagination, TeamService, TenantTeamRoleService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../../modules/permission/facades/permission.facade';
import { TeamRoleModel } from '../../../../../../models/role-permissions.model';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../listing.utils';
import { PaginationComponent } from '../../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../../view-column.component';
import { AppConfig } from '../../../../../../visa';
import { forkJoin, iif, Observable, of, zip } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../../permission-controller/root';
import { RolePermActions } from '../../../../role-permissions-controller/actions';
import { RolePermSelectors } from '../../../../role-permissions-controller/selectors';
import { RolePermStates } from '../../../../role-permissions-controller/states';

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-role-permission-team',
  templateUrl: './role-permission-team.component.html',
  styleUrls: ['./role-permission-team.component.scss']
})
export class RolePermissionTeamComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;

  protected readonly index = 2;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => state.localState.deactivateList);

  // api visa
  apiVisa = AppConfig.apiVisa;

  searchValue: any = '';

  // initial ag grid state
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowData = [
    // {
    //   teams: 'Team 1',
    //   num_users: 10,
    //   rank: 'OWNER'
    // },
    // {
    //   teams: 'Team 2',
    //   num_users: 20,
    //   rank: 'ADMIN'
    // }
  ]
  rowSelection = 'multiple';

  roleGuid: string;

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  constructor(
    private tenantTeamRoleService: TenantTeamRoleService,
    private teamService: TeamService,
    private linkSubjectToTeamService: LinkSubjectToTeamService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<{localState: LocalState}>
  ) {
    super();
  }

  /**
   * Lifecycle method: Called when component is first initialised.
   */
   ngOnInit() {
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState( {localState: a} );
    });

    this.columnsDefs = [
      {
        headerName: '',
        field: 'app_mst_link_grp_to_role.guid',
        valueFormatter: params => '',
        checkboxSelection: true,
        floatingFilter: false,
        sortable: false,
        minWidth: 0,
      },
      {
        headerName: 'Team Code',
        field: 'app_mst_grp_hdr.code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: 'Team Name',
        field: 'app_mst_grp_hdr.name',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: 'No. of Users',
        field: 'num_members',
        headerClass: 'text-right',
        cellStyle: {textAlign: 'right'}
      }
    ];
  }

  /**
   * This function is called when the user clicks on the Add button in the UI.
   * Will trigger the add component to be shown on the screen.
   *
   */
   onNext() {
    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateReturn: true,
        deactivateAdd: true,
        deactivateList: true
      });
    this.viewColFacade.onNextAndReset(this.index, 4);
  }

  /**
   * This function is called when AG grid is first initialised and initally
   * sets the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    this.subSink.sink = this.store.select(RolePermSelectors.selectGuid).subscribe(guid => {
      if (guid) {
        this.roleGuid = guid;

        const datasource = this.getDatasource();
        this.gridApi.setServerSideDatasource(datasource);

        // resize columns
        const allColIds = params.columnApi.getAllColumns().map(x => x.colId);
        params.columnApi.autoSizeColumns(allColIds);
      }
    });

    this.subSink.sink = this.store.select(RolePermSelectors.selectUpdateTeamRoleListing)
    .subscribe(x => {
      if (x) {
        if (this.gridApi.getDisplayedRowCount() > 0) {
          this.gridApi.refreshServerSideStore();
        } else {
          this.gridApi.refreshServerSideStore(
            {purge: true}
          )
        }
        this.store.dispatch(RolePermActions.setUpdateTeamRoleListing({update: false}))
      }
    })
  }

  /**
   * Returns the datasource for the AG grid.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns the datasource
   */
   getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'mst_role_guid', operator: '=', value: this.roleGuid}
    ]

    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    return {
      getRows: grid => {

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, criteria);
        this.subSink.sink = this.tenantTeamRoleService.getByCriteria(pagination, this.apiVisa)
          .pipe(
            mergeMap(a => {
              const source: Observable<TeamRoleModel>[] = [];
              a.data.forEach((b) => {
                const newModel: TeamRoleModel = new TeamRoleModel();
                newModel.app_mst_link_grp_to_role = b.app_mst_link_grp_to_role;

                const members$ = this.getMembers(b.app_mst_link_grp_to_role.mst_grp_guid.toString());
                const team$ = this.getTeam(b.app_mst_link_grp_to_role.mst_grp_guid.toString());
                const result$ = zip(members$, team$)
                  .pipe(
                    map(([members, team]) => {
                      newModel.app_mst_grp_hdr = team.data.app_mst_grp_hdr;
                      newModel.num_members = members.totalRecords;
                      return newModel;
                    })
                  )
                source.push(result$);
              })
              return iif(() => source.length > 0,
              forkJoin(source).pipe(map(x => {
              return {...a,
              data: x}
              })),
              of({...a, data: [], totalRecords: 0}))
            })
          ).subscribe(a => {
            // put data into the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
            // force pagination to go to first page if filter is applied
            if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }
            grid.successCallback(data, totalRecords);
          }, err => {
            grid.failCallback();
          })
      }
    }

  }

  /**
   * Return the team specified in grpGuid.
   *
   * @param grpGuid : the group guid to search for
   * @returns this team container model
   */
  getTeam(grpGuid: string) {
    return this.teamService.getByGuid(grpGuid, this.apiVisa)
      .pipe()
  }

  /**
   * Return the members of the group specified in appGrpGuid.
   *
   * @param appGrpGuid : the group guid to search for
   * @returns members of this group
   */
   getMembers(appGrpGuid: string) {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'app_grp_guid', operator: '=', value: appGrpGuid}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.linkSubjectToTeamService.getByCriteria(pagination, this.apiVisa)
      .pipe()
  }

  /**
   * Remove the selected teams from the role.
   */
   onRemove() {
    // find the links to delete
    const guids = this.gridApi.getSelectedNodes().map(node => node.data.app_mst_link_grp_to_role.guid.toString());
    // dispatch ngrx action to remove teams
    this.store.dispatch(RolePermActions.removeTeamsFromRoleInit({guids}));
    // deselect all nodes
    this.gridApi.deselectAll();
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_mst_link_grp_to_role hdr
   *   LEFT JOIN app_mst_grp_hdr link ON hdr.mst_grp_guid = link.guid
   *   WHERE (link.name ILIKE '%x%' OR link.code ILIKE '%x%')
   *     AND hdr.mst_role_guid = '<role>' AND hdr.status != 'DELETED'
   *
   * GET iam/team-role-links/team-to-role/query -> LinkGroupToRoleUow ->
   * generateWhereClauseByCriteria joins app_mst_grp_hdr itself and applies
   * `search_word` as a bound ILIKE over the group's code/name (and the role's
   * code/name), with `status != 'DELETED'` as its default. The `mst_role_guid`
   * scoping stays where it already was, in this component's default criteria,
   * which getDatasource concatenates with whatever is passed in -- so the search
   * no longer has to re-state it.
   *
   * Behaviour note: the keyword also matches the linked role's code and name.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * Reset the search bar.
   */
   reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
